import * as Sentry from '@sentry/react'
import { Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@repo/ui'
import { createFileRoute, redirect, useRouter } from '@tanstack/react-router'
import { Pencil, Settings, Trash } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { z } from 'zod'
import { MessageAuthor, createThreadMessage, fetchThread } from '@/api/agents/threads'
import { useAgentStream } from '@/common/useAgentStream'
import { ChatInput } from '@/components/ChatInput'
import { ErrorMessage } from '@/components/ChatMessage'
import { ChatMessageList } from '@/components/ChatMessageList'
import { DeleteThreadDialog } from '@/components/DeleteThreadDialog'
import { EditThreadDialog } from '@/components/EditThreadDialog'
import { useProjectBreadcrumb } from '@/common/useProjectBreadcrumb'
import { PageHeader } from '@/components/PageHeader'
import { resolveLegacyConversationBookmark } from '@/features/conversation/legacyRoute'

// Carries the new-thread page's attachment selection across the create-thread navigation (see
// search-agent/index.tsx's handleSend), since the message-create endpoint does not persist
// fileIds. Not server-persisted state: consumed once by the auto-stream effect below, then
// cleared from the URL so a refresh or later revisit of this thread never resends it.
// Bounded to match maxAttachedFiles (backend/internal/domain/rag_attachments.go): a hostile URL
// with an oversized or malformed fileIds array must fail validation here rather than reach the
// backend's own loop over the attachment list.
export const searchSchema = z.object({
  fileIds: z.array(z.number().int().positive()).max(5).optional()
})

export const Route = createFileRoute('/_auth/$orgSlug/$projectId/search-agent/$threadId/')({
  validateSearch: searchSchema,
  loader: async ({ params }) => {
    const projectId = parseInt(params.projectId, 10)
    const threadId = parseInt(params.threadId, 10)
    const bookmark = await resolveLegacyConversationBookmark(projectId, threadId, 'legacy-search')
    if (bookmark.disposition === 'redirect') {
      throw redirect({
        to: '/$orgSlug/$projectId/chat/$conversationId',
        params: {
          orgSlug: params.orgSlug,
          projectId: params.projectId,
          conversationId: String(bookmark.conversationId)
        },
        replace: true
      })
    }
    const thread = await fetchThread(projectId, threadId)
    return { thread, readOnly: bookmark.readOnly }
  },
  head: () => ({ meta: [{ title: 'Search Agent - Snowvault' }] }),
  component: ThreadChat
})

function ThreadChat() {
  const { orgSlug, projectId, threadId } = Route.useParams()
  const { fileIds: initialFileIds } = Route.useSearch()
  const { thread, readOnly } = Route.useLoaderData()
  const router = useRouter()
  const navigate = Route.useNavigate()
  const { projectTitle, projectTo } = useProjectBreadcrumb()
  const currentUserId = router.options.context.user.userOrgProfile?.uid ?? 0
  const globalContentEnabled = router.options.context.user.userOrgProfile?.enabled_features.global_content ?? false
  const attachmentsEnabled = router.options.context.user.userOrgProfile?.enabled_features.file_attachments ?? false

  const projectIdNum = parseInt(projectId, 10)
  const threadIdNum = parseInt(threadId, 10)

  const [showEditDialog, setShowEditDialog] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [prevThreadId, setPrevThreadId] = useState(threadIdNum)

  const {
    messages,
    streamingContent,
    statusMessage,
    isStreaming,
    error: streamError,
    clearError,
    sendMessage
  } = useAgentStream({
    endpoint: '/api/rag/searchAgentv2',
    threadMessages: thread.messages,
    currentUserId,
    router
  })

  // Clear errors when navigating between threads — the component does not
  // remount on param changes so stale error state would otherwise persist.
  if (prevThreadId !== threadIdNum) {
    setPrevThreadId(threadIdNum)
    setSaveError(null)
    clearError()
  }

  const error = saveError ?? streamError

  // Follow-up messages: save to DB first, then stream with optimistic UI showing the user's text
  const handleSend = useCallback(
    async (search: string, includeOrgContent: boolean, fileIds: Array<number>) => {
      if (readOnly) return
      setSaveError(null)
      try {
        await createThreadMessage(projectIdNum, threadIdNum, search, includeOrgContent)
      } catch (err) {
        Sentry.captureException(err, {
          extra: { userId: currentUserId, orgSlug, projectId: projectIdNum, threadId: threadIdNum }
        })
        setSaveError(err instanceof Error ? err.message : 'Failed to save message')
        return
      }
      sendMessage(search, {
        threadId: threadIdNum,
        projectId: projectIdNum,
        tagIds: [],
        fileIds,
        includeOrgContent
      })
    },
    [sendMessage, projectIdNum, threadIdNum, currentUserId, orgSlug, readOnly]
  )

  // Initial auto-stream: message already exists in threadMessages from the route loader,
  // so skip the optimistic message to avoid a duplicate bubble
  const needsAgentResponse = useMemo(() => {
    if (thread.messages.length === 0) return false
    return thread.messages.at(-1)?.author === MessageAuthor.User
  }, [thread.messages])

  const autoStreamTriggered = useRef(false)
  useEffect(() => {
    if (!readOnly && needsAgentResponse && !autoStreamTriggered.current) {
      autoStreamTriggered.current = true
      const lastMsg = thread.messages.at(-1)!
      // The message-create endpoint does not persist fileIds, so the saved message itself carries
      // no attachments. For a brand-new thread's first turn, the new-thread page's selection
      // arrives as the fileIds search param (see search-agent/index.tsx's handleSend); for any
      // other revisit of this effect (e.g. a refresh after an interrupted stream) there is no
      // saved attachment to recover, so initialFileIds is absent and none are sent. Clear the
      // param immediately so it cannot be resent on a later refresh or revisit.
      void navigate({ search: {}, replace: true })
      sendMessage(
        '',
        {
          threadId: threadIdNum,
          projectId: projectIdNum,
          tagIds: [],
          fileIds: initialFileIds ?? [],
          includeOrgContent: lastMsg.includesOrgContent
        },
        true
      )
    }
    // Empty deps fires this once per mount of the route component. Note that mount is NOT per
    // thread: this route does not remount when $threadId changes (which is exactly why the
    // prevThreadId reset above and the ChatInput key below have to exist), so an in-app switch to a
    // thread whose last saved message is still unanswered does not auto-stream. A hard load of that
    // thread does. Known defect, tracked separately rather than fixed here; widening these deps
    // needs its own coverage for the interrupted-stream and already-answered cases.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function handleEditSuccess() {
    try {
      await router.invalidate()
      setShowEditDialog(false)
    } catch {
      // invalidation failed — dialog stays open so user can retry or close manually
    }
  }

  async function handleDeleteSuccess() {
    setShowDeleteDialog(false)
    await router.invalidate()
    await router.navigate({
      to: '/$orgSlug/$projectId/search-agent',
      params: { orgSlug, projectId }
    })
  }

  return (
    <>
      <PageHeader
        breadcrumbs={[
          { label: projectTitle, to: projectTo },
          { label: 'Search Agent', to: `/${orgSlug}/${projectId}/search-agent` },
          { label: thread.title }
        ]}
        actions={
          readOnly ? undefined : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Thread settings">
                  <Settings className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setShowEditDialog(true)}>
                  <Pencil className="size-4" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setShowDeleteDialog(true)} variant="destructive">
                  <Trash className="size-4" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )
        }
      />
      {readOnly && (
        <div className="border-b bg-muted/40 px-4 py-2 text-center text-xs text-muted-foreground" role="status">
          This conversation is being moved to Chat. It is temporarily read-only.
        </div>
      )}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <ChatMessageList
          messages={messages}
          streamingContent={streamingContent}
          statusMessage={statusMessage}
          isStreaming={isStreaming}
          currentUserId={currentUserId}
          projectId={projectIdNum}
          threadId={threadIdNum}
          orgSlug={orgSlug}
          {...(readOnly ? {} : { onMessageDeleted: () => router.invalidate() })}
        />
        {error && (
          <div className="mx-auto max-w-3xl px-6 pb-2">
            <ErrorMessage message={error} />
          </div>
        )}
        {!readOnly && (
          <div className="border-t bg-background p-4">
            <div className="mx-auto max-w-3xl">
              {/* Keyed by thread: this route does not remount on a $threadId change (see the
                prevThreadId reset above), so without a key the composer's attachment selection and
                org-content toggle would follow the user into the next thread and attach a file they
                never picked there. The key also makes initialFileIds mount-scoped per thread, so a
                later revisit of this thread starts clean. */}
              <ChatInput
                key={threadIdNum}
                onSend={handleSend}
                isDisabled={isStreaming}
                defaultIncludeOrgContent={thread.messages.at(-1)?.includesOrgContent ?? true}
                globalContentEnabled={globalContentEnabled}
                attachmentsEnabled={attachmentsEnabled}
                projectId={projectIdNum}
                initialFileIds={initialFileIds}
              />
            </div>
          </div>
        )}
      </div>

      <EditThreadDialog
        key={`${threadIdNum}-${thread.title}`}
        open={showEditDialog}
        onClose={() => setShowEditDialog(false)}
        onSuccess={handleEditSuccess}
        projectId={projectIdNum}
        threadId={threadIdNum}
        currentTitle={thread.title}
      />

      <DeleteThreadDialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onSuccess={handleDeleteSuccess}
        projectId={projectIdNum}
        threadId={threadIdNum}
        threadTitle={thread.title}
      />
    </>
  )
}
